import {
  getBulkModifiersDetails,
  handlePointModifers,
  postBulkPointModifiersDraft,
  putBulkPointModifiersDraft,
} from '@/services/bulkModifiers'
import {useAppDispatch, useAppSelector} from '@/store'
import {useDropdown} from '@/hooks/Dropdown-hook'
import {DROPDOWN_TYPES} from '@/utils/constants/dropdownConstants'
import {resetBulkModifiers, setBulkModifiers} from '@/store/slices/bulkPointSlice'
import {setToastNotifications} from '@/store/slices/commonSlice'
import {ROUTES} from '@/utils/constants/routesConstants'
import {useCallback, useEffect, useState} from 'react'
import {useNavigate, useParams} from 'react-router-dom'
import {getDateFromISOString, getFormattedDateString, getUUID} from '@/utils/commons'
import {validate} from 'uuid'
export default function useReviewBulkPointModifier() {
  const dispatch = useAppDispatch()
  const navigate = useNavigate()
  const bulkModifier = useAppSelector(state => state?.bulkModifiers)
  const {dropdownList: dropdownListActions} = useDropdown({dropdownType: DROPDOWN_TYPES.Actions})

  const { id } = useParams();
  const [loading, setLoading] = useState(false);
  const handleCancel = () => {
    dispatch(resetBulkModifiers())
    navigate(ROUTES.BULK_POINT_MODIFIERS)
  }

  const getActionsValue = actions => {
    if (actions && dropdownListActions) {
      return dropdownListActions?.find(item => item.displayText === actions)?.value
    }
    return null
  }

  const getBulkModifierParams = () => {
    const existingUUID = bulkModifier?.uuid
    const modifierName = bulkModifier?.bulkModifierName || bulkModifier?.name

    return {
      storeSelectorName: bulkModifier?.storeSelector,
      name: bulkModifier?.bulkModifierName || bulkModifier?.name,
      noOfDays: bulkModifier?.numberOfDays,
      noOfPoints: bulkModifier?.numberOfPoints,
      schedule: bulkModifier?.schedule,
      startDate: bulkModifier?.startDate && getFormattedDateString(bulkModifier.startDate),
      file: bulkModifier?.storeList || bulkModifier?.file,
      action: getActionsValue(bulkModifier?.actions),
      uuid: existingUUID || getUUID(modifierName),
    }
  }

  const handlePublish = async () => {
    const param = getBulkModifierParams()
    try {
      let response = await handlePointModifers({data: param})
      if (response?.success) {
        dispatch(setBulkModifiers(bulkModifier))
        dispatch(setToastNotifications({show: true, message: 'New Bulk point modifier published', type: 'success'}))
        dispatch(resetBulkModifiers())
        navigate(ROUTES.BULK_POINT_MODIFIERS)
      } else {
        const errMsg = response?.errorMessage || 'An unknown error occurred'
        dispatch(setToastNotifications({show: true, message: errMsg, type: 'error'}))
      }
    } catch (error) {
      const errMsg = error?.response?.data?.errorMessage || error?.message || 'An unknown error occurred'
      dispatch(setToastNotifications({show: true, message: errMsg, type: 'error'}))
    }
  }

  const handleDraft = async () => {
    const existingUUID = bulkModifier?.uuid
    const param = {
      ...getBulkModifierParams(),
      status: 'DRAFT',
    }
    const callPutApi = !!existingUUID
    let response =
      callPutApi
        ? await putBulkPointModifiersDraft({data: param})
        : await postBulkPointModifiersDraft({data: param})
    if (response?.success) {
      dispatch(setToastNotifications({show: true, message: callPutApi ? 'Bulk point modifier updated' : 'New Bulk point modifier saved', type: 'success'}))
      dispatch(resetBulkModifiers())
      navigate(ROUTES.BULK_POINT_MODIFIERS)
    } else {
      dispatch(setToastNotifications({show: true, message: 'Failed to save draft', type: 'error'}))
    }
  }

  const handleGetBulkModifierDetails = useCallback(async () => {
    if (id && validate(id)) {
      return await getBulkModifiersDetails(id, 0, 10, 'uuid')
    } else {
      return await getBulkModifiersDetails(id, 0, 10, 'id')
    }
  }, [id])

  const getBulkModifierDetailsById = useCallback(async () => {
    try{
    setLoading(true)  
    const res = await handleGetBulkModifierDetails()
    setLoading(false)
    const result = res?.data[0]
    const updatedResponse = {
      actions: result?.action,
      startDate: getDateFromISOString(result?.startDate),
      schedule: result?.schedule,
      storeSelector: result?.storeSelectorName,
      bulkModifierName: result?.bulkModifierName || result?.name,
      file: result?.file,
      endDate: getDateFromISOString(result?.endDate),
      name: result?.name,
      numberOfPoints: result?.noOfPoints,
      numberOfDays: result?.noOfDays,
      uuid: result?.uuid || '',
      status: result?.status || 'Published',
    }
    dispatch(setBulkModifiers(updatedResponse))
  }catch{
    setLoading(false)
  }
  }, [handleGetBulkModifierDetails, dispatch])

  useEffect(() => {
    if (id) {
      getBulkModifierDetailsById()
    }
  }, [getBulkModifierDetailsById, id])

  return {
    handleCancel,
    handlePublish,
    handleDraft,
    loading
  }
}
